import React, { useRef, useState } from 'react';
import { CreateJourneyPanelContentProps } from './quickstart';
import { JourneySearchInput } from 'src/editor/content-creation/components/dashboard/journey-search-input';
import {
  CompanyInfo,
  RecipientSearchInput,
} from 'src/editor/content-creation/components/dashboard/recipient-search-input';
import { useProspectLogo } from 'src/common/hooks/use-prospect-logo.hook';
import { Nullable } from 'src/types/nullable.type';
import LoadingSpinnerLight from 'src/common/components/loading-spinner/light';
import { useUpdateEffect } from 'react-use';
import { Journey } from 'src/common/interfaces/journey.interface';
import { animated, useSpring, useSpringRef } from '@react-spring/web';
import * as Yup from 'yup';

export const FLOAT_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const DuplicateValidationSchema = Yup.object({
  recipientName: Yup.string(),
  name: Yup.string(),
  recipientDomain: Yup.string(),
  journeyToDuplicate: Yup.object().required(),
});

export const CreateJourneyDuplicate = ({ onChange }: CreateJourneyPanelContentProps) => {
  const selectedJourneyRef = useRef<Nullable<Journey>>();
  const recipientInputElementRef = useRef<HTMLInputElement>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<Nullable<CompanyInfo>>(null);
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');

  const { fetchProspectLogo, logoUrl, fetching, setLogoUrl, setGeneratedSlug, setDomain } = useProspectLogo();

  const onSelectRecipient = (recipient: CompanyInfo) => {
    setSelectedRecipient(recipient);
    if (recipient) {
      fetchProspectLogo(recipient.domain);
    }
  };

  const getJourneyName = (recipientName: string) => {
    const { current: selectedJourney } = selectedJourneyRef;
    if (selectedJourney) {
      if (recipientName) {
        return `${selectedJourney.name} for ${recipientName}`;
      } else {
        return selectedJourney.name;
      }
    }

    return 'Untitled Journey';
  };

  const recipientName = selectedRecipient?.name || recipientSearchQuery || '';

  useUpdateEffect(() => {
    onChange('recipientName', recipientName);
    onChange('name', getJourneyName(recipientName));
    onChange('recipientDomain', recipientSearchQuery);
  }, [recipientName, recipientSearchQuery]);

  useUpdateEffect(() => {
    if (logoUrl) {
      onChange('recipientLogoUrl', logoUrl);
    }
  }, [logoUrl]);

  const onResetRecipient = () => {
    setLogoUrl(null);
    setDomain(null);
    setGeneratedSlug(null);
  };

  const recipientLogoAnimationApi = useSpringRef();

  const [recipientLogoAnimationStyle] = useSpring(() => ({
    ref: recipientLogoAnimationApi,
    from: { opacity: 0 },
    to: { opacity: 1 },
  }));

  useUpdateEffect(() => {
    if (!recipientSearchQuery) {
      recipientLogoAnimationApi.start({ opacity: 0, config: FLOAT_ANIMATION_CONFIG });
      return;
    }
    if (fetching) {
      recipientLogoAnimationApi.start({ opacity: 1, config: FLOAT_ANIMATION_CONFIG });
    }
  }, [fetching, recipientSearchQuery]);

  const renderIconWithLoader = (url?: Nullable<string>) => {
    return (
      <animated.div
        style={recipientLogoAnimationStyle}
        className='bg-neue-journey-fg-10 rounded-lg p-2 w-10 h-10 flex-shrink-0 flex items-center justify-center'
      >
        {!url ? <LoadingSpinnerLight /> : <img src={url} className='h-6 w-auto' alt='' />}
      </animated.div>
    );
  };

  return (
    <div className='flex flex-col gap-6'>
      <p className='text-neue-journey-large'>Take a Journey you’ve created and update it.</p>
      <JourneySearchInput
        autoFocus
        onSelectJourney={(journey) => {
          selectedJourneyRef.current = journey;
          onChange('journeyToDuplicate', journey);
        }}
        optionsContainerClassName='bg-neue-journey-bg-90'
        optionClassName='bg-neue-journey-bg-90 transition-colors hover:bg-neue-journey-fg-10'
      />
      <div className='flex flex-col gap-4'>
        <p className='text-neue-journey-large'>And now pick a new recipient</p>
        <div className='flex flex-row shrink-0 gap-2'>
          <RecipientSearchInput
            inputClassName='w-[500px]'
            inputRef={recipientInputElementRef}
            placeholder="Enter recipient's website URL (optional)"
            onResetRecipient={onResetRecipient}
            onSelectRecipient={onSelectRecipient}
            onRecipientSearchQueryChange={setRecipientSearchQuery}
            hasSelectedRecipient={!!selectedRecipient}
            optionsContainerClassName='bg-neue-journey-bg-90'
            optionClassName='bg-neue-journey-bg-90 transition-colors hover:bg-neue-journey-fg-10'
          />
          {renderIconWithLoader(logoUrl)}
        </div>
      </div>
    </div>
  );
};
